import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikContextType, useFormikContext } from 'formik'
import { Stack, Columns, Column } from '@machobear/design-system'
import { format } from 'date-fns'
import { FieldInput, Dropdown, DateFieldInput } from '@/components/ds-form'
import ParkingStorageFields from './parking-storage-fields'
import { FormBox, FormSection, FormBoxSkeleton } from '../../components'
import { DetailsFormValues } from '../../types'

const FrequencyDropdown = () => {
  const { t } = useTranslation()

  return (
    <Column width="1/2">
      <Dropdown
        name="price_frequency"
        label={t('l.listing.edit.payment_frequency.label', 'Payment frequency')}
        aria-labelledby="price_frequency"
        required
        options={[
          {
            value: 'daily',
            label: t('l.listing.edit.payment_frequency_daily.text', 'Daily')
          },
          {
            value: 'weekly',
            label: t('l.listing.edit.payment_frequency_weekly.text', 'Weekly')
          },
          {
            value: 'monthly',
            label: t('l.listing.edit.payment_frequency_monthly.text', 'Monthly')
          },
          {
            value: 'one_time',
            label: t('l.listing.edit.payment_frequency_one_time.text', 'One-time')
          }
        ]}
      />
    </Column>
  )
}

const LeaseTypeDropdown = ({ province }: { province: string }) => {
  const { t } = useTranslation()

  return (
    <Column width="1/2">
      <Dropdown
        name="lease_type"
        label={t('l.listing.edit.lease_type.label', 'Lease type')}
        aria-labelledby="lease_type"
        required
        options={[
          {
            value: 'fixed',
            label: t('l.listing.edit.fixed_term.text', 'Fixed term')
          },
          ...(province !== 'AB'
            ? [
                {
                  value: 'month_to_month',
                  label: t('l.listing.edit.month_to_month.text', 'Month to month')
                }
              ]
            : [])
        ]}
      />
    </Column>
  )
}

const MIN_REASONABLE_YEAR = 2023

const AvailabilityDateInput = () => {
  const { t } = useTranslation()
  const { values, setFieldValue }: FormikContextType<DetailsFormValues> =
    useFormikContext<DetailsFormValues>()

  useEffect(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayString = format(today, 'yyyy-MM-dd')
    if (values.availability_date) {
      try {
        const availabilityDate = new Date(values.availability_date)
        if (
          !isNaN(availabilityDate.getTime()) &&
          (availabilityDate < today || availabilityDate.getFullYear() < MIN_REASONABLE_YEAR)
        ) {
          setFieldValue('availability_date', todayString)
        }
      } catch (e) {
        console.error('Error parsing availability date in effect:', e)
        setFieldValue('availability_date', todayString)
      }
    }
  }, [values.availability_date, setFieldValue])

  const getSafeDateValue = (dateString: string | undefined | null): Date | null => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (!dateString) {
      return null
    }
    try {
      const date = new Date(dateString)
      if (!isNaN(date.getTime()) && date.getFullYear() >= MIN_REASONABLE_YEAR && date >= today) {
        return date
      }
    } catch (e) {
      console.error('Error parsing date string for value prop:', e)
    }
    return null
  }

  const initialValue = getSafeDateValue(values.availability_date)

  return (
    <Column width="1/2">
      <DateFieldInput
        id="availability_date"
        name="availability_date"
        value={initialValue}
        label={t('l.listing.edit.availability_date.label', 'Availability date')}
        onChange={(date: Date | null) =>
          setFieldValue('availability_date', date ? format(date, 'yyyy-MM-dd') : null)
        }
        required
      />
    </Column>
  )
}

const MinimumLeaseFields = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext<DetailsFormValues>()

  return (
    <Stack space="gutter" align="left">
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <Dropdown
            name="lease_length"
            label={t('l.listing.edit.minimum_lease_duration.label', 'Minimum lease duration')}
            aria-labelledby="lease_length"
            options={[
              {
                value: 'no_minimum',
                label: t('l.listing.edit.no_minimum.text', 'No minimum')
              },
              {
                value: 'one_month',
                label: t('l.listing.edit.one_month.text', 'At least one month')
              },
              {
                value: 'six_months',
                label: t('l.listing.edit.six_months.text', 'At least six months')
              },
              {
                value: 'one_year',
                label: t('l.listing.edit.one_year.text', 'At least one year')
              },
              {
                value: 'other',
                label: t('common.other', 'Other')
              }
            ]}
          />
        </Column>
      </Columns>
      {values?.lease_length === 'other' && (
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput
              name="min_lease_period"
              label={t('l.listing.edit.minimum.label', 'Minimum')}
              required
            />
          </Column>
          <Column width="1/2">
            <Dropdown
              name="min_lease_period_type"
              label={t('l.listing.edit.unit.label', 'Unit')}
              aria-labelledby="min_lease_period_type"
              required
              options={[
                {
                  value: 'days',
                  label: t('l.listing.edit.days.text', 'Days')
                },
                {
                  value: 'weeks',
                  label: t('l.listing.edit.weeks.text', 'Weeks')
                },
                {
                  value: 'months',
                  label: t('l.listing.edit.months.text', 'Months')
                }
              ]}
            />
          </Column>
        </Columns>
      )}
    </Stack>
  )
}

const GroupListingFields = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext<DetailsFormValues>()

  return (
    <Stack space="xlarge">
      <FormSection label={t('Price range', 'Price range')}>
        <Stack space="gutter" align="left">
          <Columns space="gutter" collapseBelow="tablet">
            <Column width="1/2">
              <FieldInput
                name="gr_min_price"
                prefix="$"
                label={t('Minimum rental price', 'Minimum rental price')}
                type="number"
                inputMode="numeric"
                required
              />
            </Column>
            <Column width="1/2">
              <FieldInput
                name="gr_max_price"
                prefix="$"
                label={t('Maximum rental price', 'Maximum rental price')}
                type="number"
                inputMode="numeric"
                required
              />
            </Column>
          </Columns>
          <Columns space="gutter" collapseBelow="tablet">
            <FrequencyDropdown />
          </Columns>
        </Stack>
      </FormSection>
      <FormSection label={t('Lease details', 'Lease details')}>
        <Stack space="gutter" align="left">
          <Columns space="gutter" collapseBelow="tablet">
            <LeaseTypeDropdown province={values?.building?.state} />
            <AvailabilityDateInput />
          </Columns>
          <MinimumLeaseFields />
        </Stack>
      </FormSection>
    </Stack>
  )
}

const ListingFields = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext<DetailsFormValues>()

  return (
    <FormSection label={t('Rental details', 'Rental details')}>
      <Stack space="gutter" align="left">
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput
              name="price"
              prefix="$"
              label={t('Rental price', 'Rental price')}
              type="number"
              inputMode="numeric"
              required
            />
          </Column>
          <FrequencyDropdown />
        </Columns>
        <Columns space="gutter" collapseBelow="tablet">
          <LeaseTypeDropdown province={values?.building?.state} />
          <AvailabilityDateInput />
        </Columns>
        <MinimumLeaseFields />
      </Stack>
    </FormSection>
  )
}

const TermsSection = ({ isLoading }: { isLoading: boolean }) => {
  const { t } = useTranslation()
  const { values } = useFormikContext<DetailsFormValues>()

  const isMultiUnitListing = values?.isGroupListing

  return (
    <FormBox label={t('Lease Terms', 'Lease Terms')}>
      {isLoading ? (
        <FormBoxSkeleton />
      ) : (
        <>
          {isMultiUnitListing ? <GroupListingFields /> : <ListingFields />}
          <ParkingStorageFields />
        </>
      )}
    </FormBox>
  )
}

export default TermsSection
